/**
 * GridMapCanvas — Canvas 2D renderer for the grid map engine.
 * Renders terrain, walls, entities, and fog of war onto an HTML5 Canvas.
 * Supports pan/zoom with mouse wheel + drag.
 */

import { useRef, useEffect, useCallback, useState } from 'react';
import type { GridMapState, RenderConfig, CardinalDirection, FogState, MoveDirection, TriggerCondition } from '@omen-quest/grid-map';
import { coordKey } from '@omen-quest/grid-map';
import { cn } from '@/lib/utils';

// ============================================================================
// COMBAT OVERLAY TYPES
// ============================================================================

export interface TileHighlight {
  x: number;
  y: number;
  color: string;
  opacity?: number;
}

export interface PathPreviewPoint {
  x: number;
  y: number;
}

export interface TargetIndicator {
  x: number;
  y: number;
  type: 'attack' | 'spell' | 'move';
}

interface GridMapCanvasProps {
  state: GridMapState;
  config: RenderConfig;
  onCellClick?: (x: number, y: number) => void;
  onWallClick?: (x: number, y: number, dir: CardinalDirection) => void;
  onMoveRequest?: (direction: MoveDirection) => void;
  className?: string;
  /** Combat overlay: highlighted tiles (reachable, attack range, etc.) */
  highlightedTiles?: TileHighlight[];
  /** Combat overlay: path preview from entity to hovered target */
  pathPreview?: PathPreviewPoint[];
  /** Combat overlay: target indicators on specific tiles */
  targetIndicators?: TargetIndicator[];
  /** Combat overlay: ID of the currently active/selected entity (golden ring) */
  activeEntityId?: string;
}

interface ViewState {
  panX: number;
  panY: number;
  zoom: number;
}

const WALL_THICKNESS = 3;
const ENTITY_RADIUS_RATIO = 0.35;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4.0;
const MINIMAP_SIZE = 150;
const MINIMAP_MARGIN = 12;

const TAP_DISTANCE_THRESHOLD = 12;
const TAP_TIME_THRESHOLD = 250;

const TRIGGER_CONDITION_COLORS: Record<TriggerCondition, string> = {
  step: '#ef4444',      // red
  proximity: '#f59e0b', // amber
  visible: '#3b82f6',   // blue
  interact: '#22c55e',  // green
};

const TRIGGER_CONDITION_LABELS: Record<TriggerCondition, string> = {
  step: 'S',
  proximity: 'P',
  visible: 'V',
  interact: 'I',
};

function getTouchDistance(t0: Touch, t1: Touch): number {
  const dx = t0.clientX - t1.clientX;
  const dy = t0.clientY - t1.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function getTouchMidpoint(t0: Touch, t1: Touch, rect: DOMRect): { x: number; y: number } {
  return {
    x: (t0.clientX + t1.clientX) / 2 - rect.left,
    y: (t0.clientY + t1.clientY) / 2 - rect.top,
  };
}

const DPAD_DIRECTIONS: { dir: MoveDirection; label: string; gridArea: string }[] = [
  { dir: 'up', label: '▲', gridArea: '1 / 2' },
  { dir: 'left', label: '◀', gridArea: '2 / 1' },
  { dir: 'right', label: '▶', gridArea: '2 / 3' },
  { dir: 'down', label: '▼', gridArea: '3 / 2' },
];

export function GridMapCanvas({
  state, config, onCellClick, onWallClick, onMoveRequest, className,
  highlightedTiles, pathPreview, targetIndicators, activeEntityId,
}: GridMapCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const minimapRef = useRef<HTMLCanvasElement>(null);
  const [view, setView] = useState<ViewState>({ panX: 0, panY: 0, zoom: 1 });
  const dragRef = useRef<{ startX: number; startY: number; startPanX: number; startPanY: number } | null>(null);
  const touchRef = useRef<{
    mode: 'none' | 'pan' | 'pinch';
    startX?: number;
    startY?: number;
    startPanX?: number;
    startPanY?: number;
    startTime?: number;
    pinch?: {
      initialDistance: number;
      initialZoom: number;
      initialPanX: number;
      initialPanY: number;
      initialMidX: number;
      initialMidY: number;
    };
  }>({ mode: 'none' });
  const containerRef = useRef<HTMLDivElement>(null);

  const { tileSize } = config;

  // Center view on player on first render
  const initialCenterDone = useRef(false);
  useEffect(() => {
    if (!initialCenterDone.current && state && canvasRef.current) {
      const canvas = canvasRef.current;
      const player = state.map.entities.find(e => e.id === state.playerId);
      if (player) {
        setView({
          panX: canvas.width / 2 - (player.x + 0.5) * tileSize,
          panY: canvas.height / 2 - (player.y + 0.5) * tileSize,
          zoom: 1,
        });
        initialCenterDone.current = true;
      }
    }
  }, [state, tileSize]);

  // Re-center when player moves (keep player visible)
  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const player = state.map.entities.find(e => e.id === state.playerId);
    if (!player) return;

    const playerScreenX = player.x * tileSize * view.zoom + view.panX;
    const playerScreenY = player.y * tileSize * view.zoom + view.panY;
    const margin = tileSize * view.zoom * 3;

    // If player is near edge, pan to keep them visible
    let newPanX = view.panX;
    let newPanY = view.panY;
    if (playerScreenX < margin) newPanX = margin - player.x * tileSize * view.zoom;
    if (playerScreenX > canvas.width - margin) newPanX = canvas.width - margin - player.x * tileSize * view.zoom;
    if (playerScreenY < margin) newPanY = margin - player.y * tileSize * view.zoom;
    if (playerScreenY > canvas.height - margin) newPanY = canvas.height - margin - player.y * tileSize * view.zoom;

    if (newPanX !== view.panX || newPanY !== view.panY) {
      setView(v => ({ ...v, panX: newPanX, panY: newPanY }));
    }
  }, [state.moveCount, state.playerId, state.map.entities, tileSize, view.zoom, view.panX, view.panY]);

  // ============================================================================
  // MAIN CANVAS RENDER
  // ============================================================================

  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width: mapW, height: mapH, cells, entities } = state.map;
    const { fogGrid, lightGrid, openDoors, lockedDoors, playerId } = state;
    const { colors, showGrid, showDebugVis, fogEnabled } = config;
    const ts = tileSize;

    // Resize canvas to container
    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    }

    // Clear
    ctx.fillStyle = colors.void;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(view.panX, view.panY);
    ctx.scale(view.zoom, view.zoom);

    // Determine visible tile range for culling
    const startX = Math.max(0, Math.floor(-view.panX / (view.zoom * ts)) - 1);
    const startY = Math.max(0, Math.floor(-view.panY / (view.zoom * ts)) - 1);
    const endX = Math.min(mapW, Math.ceil((canvas.width - view.panX) / (view.zoom * ts)) + 1);
    const endY = Math.min(mapH, Math.ceil((canvas.height - view.panY) / (view.zoom * ts)) + 1);

    // ── Layer 1: Terrain tiles ──
    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        const cell = cells[y][x];
        const fog: FogState = fogEnabled ? fogGrid[y][x] : 'visible';

        if (fog === 'hidden') continue;

        const px = x * ts;
        const py = y * ts;

        // Base terrain color
        const isDoor = cell.terrain === 'door';
        const doorKey = isDoor ? coordKey(x, y) : '';
        const isDoorOpen = isDoor && openDoors.has(doorKey);
        const isDoorLocked = isDoor && !isDoorOpen && lockedDoors.has(doorKey);
        let color: string;
        switch (cell.terrain) {
          case 'floor': color = colors.floor; break;
          case 'wall': color = colors.wall; break;
          case 'water': color = colors.water; break;
          case 'door': color = isDoorOpen ? colors.doorOpen : isDoorLocked ? colors.doorLocked : colors.door; break;
          case 'stairs_up': color = colors.stairsUp; break;
          case 'stairs_down': color = colors.stairsDown; break;
          default: color = colors.void;
        }

        ctx.fillStyle = color;
        ctx.fillRect(px, py, ts, ts);

        // Door indicators
        if (isDoor && view.zoom > 0.4) {
          if (isDoorOpen) {
            // Open door — two short marks on the sides (door frame)
            ctx.strokeStyle = colors.wallEdge;
            ctx.lineWidth = 2;
            const inset = ts * 0.15;
            const markLen = ts * 0.2;
            // Left marks
            ctx.beginPath();
            ctx.moveTo(px + inset, py);
            ctx.lineTo(px + inset, py + markLen);
            ctx.moveTo(px + inset, py + ts);
            ctx.lineTo(px + inset, py + ts - markLen);
            // Right marks
            ctx.moveTo(px + ts - inset, py);
            ctx.lineTo(px + ts - inset, py + markLen);
            ctx.moveTo(px + ts - inset, py + ts);
            ctx.lineTo(px + ts - inset, py + ts - markLen);
            ctx.stroke();
          } else if (isDoorLocked) {
            // Locked door — X mark over center
            ctx.strokeStyle = '#ff4444';
            ctx.lineWidth = 2;
            const m = ts * 0.25;
            ctx.beginPath();
            ctx.moveTo(px + m, py + m);
            ctx.lineTo(px + ts - m, py + ts - m);
            ctx.moveTo(px + ts - m, py + m);
            ctx.lineTo(px + m, py + ts - m);
            ctx.stroke();
          } else {
            // Closed door — horizontal bar across center
            ctx.strokeStyle = colors.wallEdge;
            ctx.lineWidth = 2.5;
            const margin = ts * 0.15;
            ctx.beginPath();
            ctx.moveTo(px + margin, py + ts / 2);
            ctx.lineTo(px + ts - margin, py + ts / 2);
            ctx.stroke();
          }
        }

        // Light modulation for visible cells
        if (fog === 'visible') {
          const light = lightGrid[y][x];
          if (light < 1.0) {
            ctx.fillStyle = `rgba(0, 0, 0, ${(1 - light) * 0.5})`;
            ctx.fillRect(px, py, ts, ts);
          }
        }

        // Explored overlay (dimmed)
        if (fog === 'explored') {
          ctx.fillStyle = colors.fogExplored;
          ctx.fillRect(px, py, ts, ts);
        }
      }
    }

    // ── Layer 2: Grid lines ──
    if (showGrid && view.zoom > 0.5) {
      ctx.strokeStyle = colors.gridLine;
      ctx.lineWidth = 0.5;
      for (let y = startY; y <= endY; y++) {
        ctx.beginPath();
        ctx.moveTo(startX * ts, y * ts);
        ctx.lineTo(endX * ts, y * ts);
        ctx.stroke();
      }
      for (let x = startX; x <= endX; x++) {
        ctx.beginPath();
        ctx.moveTo(x * ts, startY * ts);
        ctx.lineTo(x * ts, endY * ts);
        ctx.stroke();
      }
    }

    // ── Layer 3: Wall edges ──
    ctx.strokeStyle = colors.wallEdge;
    ctx.lineWidth = WALL_THICKNESS;
    ctx.lineCap = 'round';

    for (let y = startY; y < endY; y++) {
      for (let x = startX; x < endX; x++) {
        const fog: FogState = fogEnabled ? fogGrid[y][x] : 'visible';
        if (fog === 'hidden') continue;

        const cell = cells[y][x];
        const px = x * ts;
        const py = y * ts;

        if (cell.walls.N) {
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px + ts, py);
          ctx.stroke();
        }
        if (cell.walls.E) {
          ctx.beginPath();
          ctx.moveTo(px + ts, py);
          ctx.lineTo(px + ts, py + ts);
          ctx.stroke();
        }
        if (cell.walls.S) {
          ctx.beginPath();
          ctx.moveTo(px, py + ts);
          ctx.lineTo(px + ts, py + ts);
          ctx.stroke();
        }
        if (cell.walls.W) {
          ctx.beginPath();
          ctx.moveTo(px, py);
          ctx.lineTo(px, py + ts);
          ctx.stroke();
        }
      }
    }

    // ── Layer 3b: Combat overlay — Highlighted tiles ──
    if (highlightedTiles && highlightedTiles.length > 0) {
      for (const tile of highlightedTiles) {
        const fog: FogState = fogEnabled ? (fogGrid[tile.y]?.[tile.x] ?? 'hidden') : 'visible';
        if (fog === 'hidden') continue;

        const px = tile.x * ts;
        const py = tile.y * ts;
        const alpha = tile.opacity ?? 0.25;
        ctx.fillStyle = tile.color.startsWith('rgba') ? tile.color : `${tile.color}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;
        ctx.fillRect(px + 1, py + 1, ts - 2, ts - 2);
      }
    }

    // ── Layer 3c: Combat overlay — Path preview ──
    if (pathPreview && pathPreview.length >= 2) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(pathPreview[0].x * ts + ts / 2, pathPreview[0].y * ts + ts / 2);
      for (let i = 1; i < pathPreview.length; i++) {
        ctx.lineTo(pathPreview[i].x * ts + ts / 2, pathPreview[i].y * ts + ts / 2);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      // Dot at endpoint
      const end = pathPreview[pathPreview.length - 1];
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.beginPath();
      ctx.arc(end.x * ts + ts / 2, end.y * ts + ts / 2, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Layer 3d: Combat overlay — Target indicators ──
    if (targetIndicators && targetIndicators.length > 0) {
      for (const indicator of targetIndicators) {
        const px = indicator.x * ts;
        const py = indicator.y * ts;

        switch (indicator.type) {
          case 'attack': {
            // Red crosshair
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.8)';
            ctx.lineWidth = 2;
            const cx = px + ts / 2;
            const cy = py + ts / 2;
            const r = ts * 0.35;
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - r - 2, cy); ctx.lineTo(cx + r + 2, cy);
            ctx.moveTo(cx, cy - r - 2); ctx.lineTo(cx, cy + r + 2);
            ctx.stroke();
            break;
          }
          case 'spell': {
            // Purple diamond
            ctx.strokeStyle = 'rgba(168, 85, 247, 0.8)';
            ctx.lineWidth = 2;
            const cx = px + ts / 2;
            const cy = py + ts / 2;
            const r = ts * 0.35;
            ctx.beginPath();
            ctx.moveTo(cx, cy - r);
            ctx.lineTo(cx + r, cy);
            ctx.lineTo(cx, cy + r);
            ctx.lineTo(cx - r, cy);
            ctx.closePath();
            ctx.stroke();
            break;
          }
          case 'move': {
            // Blue dot
            ctx.fillStyle = 'rgba(59, 130, 246, 0.6)';
            ctx.beginPath();
            ctx.arc(px + ts / 2, py + ts / 2, ts * 0.15, 0, Math.PI * 2);
            ctx.fill();
            break;
          }
        }
      }
    }

    // ── Layer 4: Entities ──
    const entityRadius = ts * ENTITY_RADIUS_RATIO;

    for (const entity of entities) {
      const fog: FogState = fogEnabled ? (fogGrid[entity.y]?.[entity.x] ?? 'hidden') : 'visible';
      // Always show player, only show others if visible
      if (entity.id !== playerId && fog !== 'visible') continue;

      const ex = entity.x * ts + ts / 2;
      const ey = entity.y * ts + ts / 2;

      let tokenColor: string;
      switch (entity.type) {
        case 'player': tokenColor = colors.playerToken; break;
        case 'npc': tokenColor = colors.npcToken; break;
        case 'monster': tokenColor = colors.monsterToken; break;
        default: tokenColor = colors.objectToken;
      }

      ctx.fillStyle = tokenColor;
      ctx.beginPath();
      ctx.arc(ex, ey, entityRadius, 0, Math.PI * 2);
      ctx.fill();

      // Active entity glow ring
      if (activeEntityId && entity.id === activeEntityId) {
        ctx.strokeStyle = 'rgba(255, 215, 0, 0.9)';
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(ex, ey, entityRadius + 3, 0, Math.PI * 2);
        ctx.stroke();
        // Outer glow
        ctx.strokeStyle = 'rgba(255, 215, 0, 0.3)';
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(ex, ey, entityRadius + 6, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Dark outline
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // First letter of name
      if (view.zoom > 0.6) {
        ctx.fillStyle = '#000';
        ctx.font = `bold ${Math.round(ts * 0.4)}px monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(entity.name[0] ?? '?', ex, ey);
      }
    }

    // ── Layer 5: Trigger indicators ──
    const triggers = state.map.triggers ?? [];
    if (triggers.length > 0) {
      const { showTriggers } = config;
      const { firedTriggers } = state;

      // Build a lookup: coordKey → trigger[] for visible-range cells
      const triggerCellMap = new Map<string, typeof triggers>();
      for (const trigger of triggers) {
        for (const cell of trigger.cells) {
          const key = coordKey(cell.x, cell.y);
          const arr = triggerCellMap.get(key);
          if (arr) arr.push(trigger);
          else triggerCellMap.set(key, [trigger]);
        }
      }

      for (let y = startY; y < endY; y++) {
        for (let x = startX; x < endX; x++) {
          const fog: FogState = fogEnabled ? fogGrid[y][x] : 'visible';
          if (fog === 'hidden') continue;

          const key = coordKey(x, y);
          const cellTriggers = triggerCellMap.get(key);
          if (!cellTriggers) continue;

          const px = x * ts;
          const py = y * ts;

          for (const trigger of cellTriggers) {
            const isFired = (trigger.oneShot !== false) && firedTriggers.has(trigger.id);

            // Debug overlay: show all triggers with colored borders
            if (showTriggers) {
              const color = TRIGGER_CONDITION_COLORS[trigger.condition];
              ctx.strokeStyle = isFired ? 'rgba(100,100,100,0.3)' : color;
              ctx.lineWidth = 2;
              ctx.setLineDash([4, 3]);
              ctx.strokeRect(px + 2, py + 2, ts - 4, ts - 4);
              ctx.setLineDash([]);

              // Condition label in top-left corner
              if (view.zoom > 0.5) {
                ctx.fillStyle = isFired ? 'rgba(100,100,100,0.4)' : color;
                ctx.font = `bold ${Math.round(ts * 0.3)}px monospace`;
                ctx.textAlign = 'left';
                ctx.textBaseline = 'top';
                ctx.fillText(TRIGGER_CONDITION_LABELS[trigger.condition], px + 3, py + 2);
              }

              // Proximity radius diamond
              if (trigger.condition === 'proximity' && trigger.radius && !isFired) {
                const r = trigger.radius;
                const cx = px + ts / 2;
                const cy = py + ts / 2;
                ctx.strokeStyle = color + '40'; // 25% opacity
                ctx.lineWidth = 1;
                ctx.setLineDash([3, 3]);
                ctx.beginPath();
                ctx.moveTo(cx, cy - r * ts);
                ctx.lineTo(cx + r * ts, cy);
                ctx.lineTo(cx, cy + r * ts);
                ctx.lineTo(cx - r * ts, cy);
                ctx.closePath();
                ctx.stroke();
                ctx.setLineDash([]);
              }
            }

            // Always-visible trigger icons (for trigger.visible: true, in FOV)
            if (trigger.visible && fog === 'visible' && !isFired && view.zoom > 0.4) {
              const icon = trigger.icon ?? '?';
              const iconColor = trigger.color ?? '#ffffff';
              const cx = px + ts / 2;
              const cy = py + ts / 2;

              // Subtle glow
              ctx.shadowColor = iconColor;
              ctx.shadowBlur = ts * 0.3;
              ctx.fillStyle = iconColor;
              ctx.font = `bold ${Math.round(ts * 0.5)}px monospace`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(icon, cx, cy);
              ctx.shadowColor = 'transparent';
              ctx.shadowBlur = 0;
            }
          }
        }
      }
    }

    // ── Layer 6: Debug visibility overlay ──
    if (showDebugVis) {
      const debugFontSize = Math.round(ts * 0.2);
      ctx.font = `${debugFontSize}px monospace`;
      ctx.textAlign = 'center';

      const player = entities.find(e => e.id === playerId);
      const px = player?.x ?? 0;
      const py = player?.y ?? 0;

      for (let y = startY; y < endY; y++) {
        for (let x = startX; x < endX; x++) {
          const fog: FogState = fogEnabled ? fogGrid[y][x] : 'visible';
          if (fog !== 'visible') continue;

          const light = lightGrid[y][x];
          const cx = x * ts + ts / 2;
          const topY = y * ts + debugFontSize + 1;

          // Row 1: cell coordinates
          ctx.fillStyle = 'rgba(0, 200, 255, 0.7)';
          ctx.fillText(`${x},${y}`, cx, topY);

          // Row 2: relative position to player
          const dx = x - px;
          const dy = y - py;
          ctx.fillStyle = 'rgba(255, 200, 0, 0.7)';
          ctx.fillText(`${dx >= 0 ? '+' : ''}${dx},${dy >= 0 ? '+' : ''}${dy}`, cx, topY + debugFontSize + 1);

          // Row 3: visibility value
          ctx.fillStyle = 'rgba(0, 255, 0, 0.7)';
          ctx.fillText(light.toFixed(2), cx, topY + (debugFontSize + 1) * 2);
        }
      }
    }

    ctx.restore();
  }, [state, config, view, tileSize, highlightedTiles, pathPreview, targetIndicators, activeEntityId]);

  // ============================================================================
  // MINIMAP RENDER
  // ============================================================================

  const renderMinimap = useCallback(() => {
    const canvas = minimapRef.current;
    if (!canvas || !config.showMinimap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { width: mapW, height: mapH, entities } = state.map;
    const { fogGrid, openDoors, lockedDoors } = state;
    const { fogEnabled, colors } = config;

    canvas.width = MINIMAP_SIZE;
    canvas.height = MINIMAP_SIZE;

    const scale = Math.min(MINIMAP_SIZE / mapW, MINIMAP_SIZE / mapH);
    const offsetX = (MINIMAP_SIZE - mapW * scale) / 2;
    const offsetY = (MINIMAP_SIZE - mapH * scale) / 2;

    // Background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.fillRect(0, 0, MINIMAP_SIZE, MINIMAP_SIZE);

    // Cells
    for (let y = 0; y < mapH; y++) {
      for (let x = 0; x < mapW; x++) {
        const fog: FogState = fogEnabled ? fogGrid[y][x] : 'visible';
        if (fog === 'hidden') continue;

        const cell = state.map.cells[y][x];
        if (cell.terrain === 'wall' || cell.terrain === 'void') {
          ctx.fillStyle = fog === 'visible' ? '#333' : '#1a1a1a';
        } else if (cell.terrain === 'door') {
          const dk = coordKey(x, y);
          const doorIsOpen = openDoors.has(dk);
          const doorIsLocked = !doorIsOpen && lockedDoors.has(dk);
          ctx.fillStyle = fog === 'visible'
            ? (doorIsOpen ? '#887740' : doorIsLocked ? '#662222' : '#554420')
            : '#332a10';
        } else {
          ctx.fillStyle = fog === 'visible' ? '#666' : '#333';
        }
        ctx.fillRect(offsetX + x * scale, offsetY + y * scale, Math.ceil(scale), Math.ceil(scale));
      }
    }

    // Player dot
    const player = entities.find(e => e.id === state.playerId);
    if (player) {
      ctx.fillStyle = colors.playerToken;
      const px = offsetX + player.x * scale + scale / 2;
      const py = offsetY + player.y * scale + scale / 2;
      ctx.beginPath();
      ctx.arc(px, py, Math.max(2, scale * 1.5), 0, Math.PI * 2);
      ctx.fill();
    }

    // Viewport rectangle
    if (canvasRef.current) {
      const mainCanvas = canvasRef.current;
      const ts = tileSize * view.zoom;
      const vx = -view.panX / ts;
      const vy = -view.panY / ts;
      const vw = mainCanvas.width / ts;
      const vh = mainCanvas.height / ts;

      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 1;
      ctx.strokeRect(
        offsetX + vx * scale,
        offsetY + vy * scale,
        vw * scale,
        vh * scale,
      );
    }
  }, [state, config, view, tileSize]);

  // ============================================================================
  // RENDER LOOP
  // ============================================================================

  useEffect(() => {
    render();
    renderMinimap();
  }, [render, renderMinimap]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(() => {
      render();
      renderMinimap();
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [render, renderMinimap]);

  // ============================================================================
  // INPUT HANDLERS
  // ============================================================================

  // Zoom with mouse wheel — native listener with { passive: false }
  // so preventDefault() works (React onWheel delegates to the passive root)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;

      setView(prev => {
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, prev.zoom * zoomFactor));
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const scale = newZoom / prev.zoom;
        return {
          zoom: newZoom,
          panX: mouseX - (mouseX - prev.panX) * scale,
          panY: mouseY - (mouseY - prev.panY) * scale,
        };
      });
    }

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  // Pan with mouse drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0 || e.button === 1) { // Left or middle click
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startPanX: view.panX,
        startPanY: view.panY,
      };
    }
  }, [view.panX, view.panY]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    setView(prev => ({
      ...prev,
      panX: drag.startPanX + dx,
      panY: drag.startPanY + dy,
    }));
  }, []);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = Math.abs(e.clientX - drag.startX);
    const dy = Math.abs(e.clientY - drag.startY);
    const wasDrag = dx > 3 || dy > 3;

    if (!wasDrag && e.button === 0) {
      // Click — convert to grid coords
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        const worldX = (e.clientX - rect.left - view.panX) / (view.zoom * tileSize);
        const worldY = (e.clientY - rect.top - view.panY) / (view.zoom * tileSize);
        const cellX = Math.floor(worldX);
        const cellY = Math.floor(worldY);

        if (cellX >= 0 && cellX < state.map.width && cellY >= 0 && cellY < state.map.height) {
          // Determine if click is near a wall edge
          const inCellX = worldX - cellX;
          const inCellY = worldY - cellY;
          const edgeThreshold = 0.2;

          if (onWallClick) {
            if (inCellY < edgeThreshold) {
              onWallClick(cellX, cellY, 'N');
            } else if (inCellY > 1 - edgeThreshold) {
              onWallClick(cellX, cellY, 'S');
            } else if (inCellX < edgeThreshold) {
              onWallClick(cellX, cellY, 'W');
            } else if (inCellX > 1 - edgeThreshold) {
              onWallClick(cellX, cellY, 'E');
            } else if (onCellClick) {
              onCellClick(cellX, cellY);
            }
          } else if (onCellClick) {
            onCellClick(cellX, cellY);
          }
        }
      }
    }

    dragRef.current = null;
  }, [view, tileSize, state.map.width, state.map.height, onCellClick, onWallClick]);

  // ============================================================================
  // TOUCH HANDLERS
  // ============================================================================

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch mode
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const mid = getTouchMidpoint(e.touches[0], e.touches[1], rect);
      touchRef.current = {
        mode: 'pinch',
        pinch: {
          initialDistance: getTouchDistance(e.touches[0], e.touches[1]),
          initialZoom: view.zoom,
          initialPanX: view.panX,
          initialPanY: view.panY,
          initialMidX: mid.x,
          initialMidY: mid.y,
        },
      };
    } else if (e.touches.length === 1) {
      // Pan mode
      touchRef.current = {
        mode: 'pan',
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        startPanX: view.panX,
        startPanY: view.panY,
        startTime: Date.now(),
      };
    }
  }, [view.panX, view.panY, view.zoom]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const t = touchRef.current;
    if (t.mode === 'pan' && t.startX != null && t.startY != null && t.startTime != null) {
      // Check if this was a tap (short distance, short time)
      const touch = e.changedTouches[0];
      const dx = Math.abs(touch.clientX - t.startX);
      const dy = Math.abs(touch.clientY - t.startY);
      const elapsed = Date.now() - t.startTime;

      if (dx <= TAP_DISTANCE_THRESHOLD && dy <= TAP_DISTANCE_THRESHOLD && elapsed < TAP_TIME_THRESHOLD) {
        // Convert to grid coords (same logic as mouse click)
        const rect = canvasRef.current?.getBoundingClientRect();
        if (rect) {
          const worldX = (touch.clientX - rect.left - view.panX) / (view.zoom * tileSize);
          const worldY = (touch.clientY - rect.top - view.panY) / (view.zoom * tileSize);
          const cellX = Math.floor(worldX);
          const cellY = Math.floor(worldY);

          if (cellX >= 0 && cellX < state.map.width && cellY >= 0 && cellY < state.map.height) {
            const inCellX = worldX - cellX;
            const inCellY = worldY - cellY;
            const edgeThreshold = 0.2;

            if (onWallClick) {
              if (inCellY < edgeThreshold) {
                onWallClick(cellX, cellY, 'N');
              } else if (inCellY > 1 - edgeThreshold) {
                onWallClick(cellX, cellY, 'S');
              } else if (inCellX < edgeThreshold) {
                onWallClick(cellX, cellY, 'W');
              } else if (inCellX > 1 - edgeThreshold) {
                onWallClick(cellX, cellY, 'E');
              } else if (onCellClick) {
                onCellClick(cellX, cellY);
              }
            } else if (onCellClick) {
              onCellClick(cellX, cellY);
            }
          }
        }
      }
    }
    touchRef.current = { mode: 'none' };
  }, [view, tileSize, state.map.width, state.map.height, onCellClick, onWallClick]);

  // Imperative touchmove with { passive: false } for reliable preventDefault
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleTouchMove(e: TouchEvent) {
      const t = touchRef.current;

      if (t.mode === 'pinch' && t.pinch && e.touches.length === 2) {
        e.preventDefault();
        const rect = canvas!.getBoundingClientRect();
        const dist = getTouchDistance(e.touches[0], e.touches[1]);
        const mid = getTouchMidpoint(e.touches[0], e.touches[1], rect);
        const p = t.pinch;

        const zoomFactor = dist / p.initialDistance;
        const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, p.initialZoom * zoomFactor));
        const zoomRatio = newZoom / p.initialZoom;

        setView({
          zoom: newZoom,
          panX: mid.x - (p.initialMidX - p.initialPanX) * zoomRatio,
          panY: mid.y - (p.initialMidY - p.initialPanY) * zoomRatio,
        });
      } else if (t.mode === 'pan' && t.startX != null && t.startY != null && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - t.startX;
        const dy = e.touches[0].clientY - t.startY;
        setView(prev => ({
          ...prev,
          panX: t.startPanX! + dx,
          panY: t.startPanY! + dy,
        }));
      }
    }

    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => canvas.removeEventListener('touchmove', handleTouchMove);
  }, []); // Stable — reads touchRef via ref, setView is stable

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-hidden', className)}
      onContextMenu={e => e.preventDefault()}
    >
      <canvas
        ref={canvasRef}
        className="block w-full h-full cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { dragRef.current = null; }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      />
      {config.showMinimap && (
        <canvas
          ref={minimapRef}
          className="absolute bottom-3 left-3 border border-white/20 rounded"
          width={MINIMAP_SIZE}
          height={MINIMAP_SIZE}
          style={{ width: MINIMAP_SIZE, height: MINIMAP_SIZE }}
        />
      )}
      {/* D-pad overlay — visible only on touch devices */}
      {onMoveRequest && (
        <div
          className="absolute bottom-4 right-4 hidden [@media(pointer:coarse)]:grid"
          style={{
            gridTemplateColumns: 'repeat(3, 2.75rem)',
            gridTemplateRows: 'repeat(3, 2.75rem)',
            gap: '0.25rem',
          }}
        >
          {DPAD_DIRECTIONS.map(({ dir, label, gridArea }) => (
            <button
              key={dir}
              onPointerDown={(e) => {
                e.preventDefault();
                onMoveRequest(dir);
              }}
              className="flex items-center justify-center rounded-lg bg-white/10 active:bg-white/25 text-white text-lg select-none"
              style={{ gridArea }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
